import React, {
  useCallback,
  useMemo,
  useState,
  FC,
  PropsWithChildren,
} from 'react';

import { useAlloyFeatureFlag, useCurrentProjectId } from '@adsk/alloy-provider';
import { getOrCreateGlobalContext } from '@adsk/alloy-react-context';

import { getSpecs } from '../api/entities';
import { SpecRawData } from '../types';

interface SubmittalsContextData {
  specs: {
    data: SpecRawData[];
    isLoading: boolean;
    error?: Error;
    fetchSpecs: () => void;
  };
}

export const SubmittalsContext =
  getOrCreateGlobalContext<SubmittalsContextData>('SubmittalsContext');

const SubmittalsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [specs, setSpecs] = useState<SpecRawData[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState(undefined);
  const projectId = useCurrentProjectId();
  const { value: isCustomReviewEnabled } = useAlloyFeatureFlag(
    'submittals-crw-fe',
    true,
  );

  const fetchSpecs = useCallback(() => {
    if (!projectId || specs !== null) {
      return;
    }

    setIsLoading(true);

    getSpecs(projectId, isCustomReviewEnabled)
      .then((specs) => {
        setSpecs(specs);
        setIsLoading(false);
      })
      .catch((e) => {
        setError(e);
        setIsLoading(false);
      });
  }, [projectId, specs, isCustomReviewEnabled]);

  const value = useMemo(
    () => ({
      specs: {
        data: specs || [],
        isLoading,
        error,
        fetchSpecs,
      },
    }),
    [specs, isLoading, error, fetchSpecs],
  );

  return (
    <SubmittalsContext.Provider value={value}>
      {children}
    </SubmittalsContext.Provider>
  );
};

SubmittalsProvider.displayName = 'SubmittalsProvider';

export default SubmittalsProvider;
